import React, { useRef, useEffect } from 'react'
import { colors } from './fieldTheme'
import { MinusIcon, PlusIcon, GapMark, InfoIcon } from './FieldIcons'

// Ligne d'objet avec compteur (canvas W8, décisions 25 et 26).
// Les objets ne bougent jamais : un écart se marque sur la ligne (fond, mot « Écart », « 4 / 6 »).
// Charte : pas de « · » dans une liste, d'où « Écart : 4 / 6 » et une ligne par référence.
// La hauteur d'une ligne ne change jamais pendant le comptage (retour d'Alexandra du 24/10/2026) :
// l'écart prend la place de la première ligne de référence, qu'il contient déjà (« 4 / 6 »).

const LINE_HEIGHT = 20

const counterButton = (disabled) => ({
	backgroundImage: 'none',
	backgroundColor: '#E8EBF5',
	width: 52,
	height: 52,
	borderRadius: 999,
	border: '2px solid ' + colors.border,
	color: colors.primary,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: 0,
	flex: 'none',
	cursor: disabled ? 'not-allowed' : 'pointer',
	opacity: disabled ? 0.4 : 1
})

// Appui sur « - » ou « + » : la quantité change de 1 dès le toucher (pas au relâchement) ; maintenu plus de HOLD_DELAY_MS, le
// compteur défile, de plus en plus vite (de quoi atteindre 200 en quelques secondes sans dépasser la cible). Les paliers sont
// comptés depuis le début du défilement : [durée du palier en ms, délai entre deux pas en ms].
const HOLD_DELAY_MS = 1000
const HOLD_STAGES = [[2000, 62], [3000, 35], [Infinity, 20]]

function stepDelay(elapsed)
{
	let left = elapsed
	for (let i = 0; i < HOLD_STAGES.length; i++)
	{
		if (left < HOLD_STAGES[i][0])
			return HOLD_STAGES[i][1]
		left -= HOLD_STAGES[i][0]
	}
	return HOLD_STAGES[HOLD_STAGES.length - 1][1]
}

function CounterButton({ label, delta, value, disabled, onChange, children })
{
	const current = useRef(value)
	current.current = value
	const timer = useRef(null)
	const stop = () => {
		clearTimeout(timer.current)
		timer.current = null
	}
	useEffect(() => stop, [])
	const step = () => {
		const next = Math.max(0, current.current + delta)
		if (next === current.current)
			return false
		current.current = next
		onChange(next)
		return true
	}
	const start = (event) => {
		if (disabled || (event.button !== undefined && event.button > 0))
			return ;
		stop()
		step()
		timer.current = setTimeout(() => {
			const began = Date.now()
			const tick = () => {
				if (!step())
				{
					timer.current = null
					return ;
				}
				timer.current = setTimeout(tick, stepDelay(Date.now() - began))
			}
			tick()
		}, HOLD_DELAY_MS)
	}
	return (
		<button
			type="button"
			aria-label={label}
			disabled={disabled}
			onClick={(event) => {
				// Le toucher ou le clic a déjà compté (onPointerDown) ; seul le clavier (detail 0) passe par ici
				if (event.detail === 0)
					step()
			}}
			onPointerDown={start}
			onPointerUp={stop}
			onPointerLeave={stop}
			onPointerCancel={stop}
			onBlur={stop}
			onContextMenu={(event) => event.preventDefault()}
			style={{ ...counterButton(disabled), userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', touchAction: 'manipulation' }}
		>
			{children}
		</button>
	)
}

export default function FieldItemRow({ object, value, reference, referenceLines, missingLabel, gap, first, minHeight = 72, onChange })
{
	const atZero = value <= 0
	return (
		<div style={{ minHeight, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderTop: first ? 0 : '1px solid ' + colors.separator, background: gap ? '#FEE2E2' : colors.surface }}>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, lineHeight: 1.3 }}>
				<span lang="fr" style={{ fontSize: 18, fontWeight: 700, overflowWrap: 'break-word', hyphens: 'auto', WebkitHyphens: 'auto' }}>{object.name}</span>
				{referenceLines.map((line, index) => {
					if (gap && index === 0)
					{
						return (
							<span key="gap" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: LINE_HEIGHT, color: '#7F1D1D', fontSize: 15, fontWeight: 800 }}>
								<GapMark size={18} />
								{'Écart : ' + value + ' / ' + reference}
							</span>
						)
					}
					return <span key={line} style={{ height: LINE_HEIGHT, fontSize: 15, fontWeight: 600, color: colors.textSecondary }}>{line}</span>
				})}
				{missingLabel && (
					<span style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 30, padding: '0 10px 0 6px', borderRadius: 999, background: '#FFEDD5', color: '#7C2D12', fontSize: 15, fontWeight: 700 }}>
						<InfoIcon />
						{missingLabel}
					</span>
				)}
			</span>
			<span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
				<CounterButton label={'Retirer : ' + object.name} delta={-1} value={value} disabled={atZero} onChange={onChange}>
					<MinusIcon />
				</CounterButton>
				<output aria-live="polite" aria-label={object.name + ' : ' + value} style={{ minWidth: 36, textAlign: 'center', fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{value}</output>
				<CounterButton label={'Ajouter : ' + object.name} delta={1} value={value} disabled={false} onChange={onChange}>
					<PlusIcon />
				</CounterButton>
			</span>
		</div>
	)
}
